import React, { useEffect, useRef } from 'react';
import { UPLOADS_BASE_URL } from '../services/api';

const isVideo = (filename) => {
    const ext = filename?.split('.').pop().toLowerCase();
    return ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v', 'ogv'].includes(ext);
};

const TIER_STYLES = {
    local: { dot: 'bg-gray-400',   label: 'Local storage', text: 'text-gray-300',   bg: 'bg-white/10' },
    b2:    { dot: 'bg-amber-400',  label: 'B2 (cloud)',    text: 'text-amber-300',  bg: 'bg-amber-400/20' },
    minio: { dot: 'bg-blue-400',   label: 'MinIO (cold)',  text: 'text-blue-300',   bg: 'bg-blue-400/20' },
    agent: { dot: 'bg-emerald-400',label: 'Agent node',    text: 'text-emerald-300',bg: 'bg-emerald-400/20' },
};

const StorageBadge = ({ location }) => {
    const s = TIER_STYLES[location];
    if (!s) return null;
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${s.bg} ${s.text}`}>
            <span className={`w-2 h-2 rounded-full ${s.dot}`} />
            {s.label}
        </span>
    );
};

const ImageLightbox = ({ image, imageUrl, onClose, onNext, onPrev }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight' && onNext) onNext();
            if (e.key === 'ArrowLeft' && onPrev) onPrev();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose, onNext, onPrev]);

    // Pause video when navigating away
    useEffect(() => {
        return () => { if (videoRef.current) videoRef.current.pause(); };
    }, [image]);

    if (!image) return null;

    const mediaUrl = imageUrl || `${UPLOADS_BASE_URL}/uploads/${image.path}`;
    const video = isVideo(image.filename);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
            onClick={onClose}>
            {/* Close */}
            <button onClick={onClose}
                className="absolute top-5 right-5 w-9 h-9 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition z-10">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            {/* Prev */}
            {onPrev && (
                <button onClick={e => { e.stopPropagation(); onPrev(); }}
                    className="absolute left-5 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition z-10">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            )}

            {/* Next */}
            {onNext && (
                <button onClick={e => { e.stopPropagation(); onNext(); }}
                    className="absolute right-5 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition z-10">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            )}

            {/* Media */}
            <div className="flex flex-col items-center max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
                {video ? (
                    <video
                        ref={videoRef}
                        src={mediaUrl}
                        controls
                        autoPlay
                        className="max-w-full max-h-[80vh] rounded-lg shadow-2xl bg-black"
                        style={{ minWidth: 320 }}
                    />
                ) : (
                    <img
                        src={mediaUrl}
                        alt={image.filename}
                        className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                    />
                )}
                <div className="mt-3 text-center">
                    <p className="text-white text-sm font-medium">{image.filename}</p>
                    <p className="text-white/50 text-xs mt-0.5">{new Date(image.created_at).toLocaleDateString()}</p>
                    {image.storage_location && (
                        <div className="mt-2 flex justify-center">
                            <StorageBadge location={image.storage_location} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImageLightbox;
